import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Badge, Button, Checkbox, Drawer, Radio, Select, SelectProps, Tooltip, Typography } from "antd";
import { each, includes, keys, map } from "underscore";
import Layout from "@components/wrappers/layouts/main-layout/Layout";
import {
    ISkusData,
    LayoutProps,
    IFixedReviewOptions,
    IValueString,
    TActiveClientId,
    IChannel,
    ReportFilters,
    OptionValues,
    ReportFiltersType,
    SubModule,
    IMainContext,
    FrequencySlicerOptions,
    ProductDeepdiveModules,
    ICategoryInsightFilters,
} from "@ts/common-types";
import AgGridLoaderComponent from "@components/loaders/AgGridLoaderComponent";
import {
    fetchCategoryAnalysisFiltersData,
    fetchReviewsBrandData,
    fetchReviewsSkuData,
    getReviewsFiltersData,
} from "@ts/retrieve-data";
import { showToast } from "@ts/modals";
import { formatCategoriesFilters, formatSkusFilters } from "@ts/widget";
import {
    DATE_COMPARISON_OPTIONS,
    mainContext,
    REVIEW_FILTER_MAX_LIMIT,
    SKU_DEPENDENT_MODULES,
} from "@ts/constants";
import {
    getActiveModule,
    getClientTagsList,
    getQueryParams,
    setQueryParams,
    titleCase,
} from "@ts/utils";
import {
    formatCategoryInsightsFilters, getCategoryInsightsFilters,
    getCategoryMatchingBrands,
    getCategoryMatchingSubCategories
} from "@/ts/widget";
import { groupValuesByFeature } from "@/ts/widget";
import { DefaultOptionType } from "antd/es/select";
import * as Sentry from "@sentry/react";
import { GiSettingsKnobs } from "react-icons/gi";
import { usePreviousValue } from "@/hooks/usePreviousValue";

const { Title, Text } = Typography;

interface IReportFiltersLayout extends LayoutProps {
    reviewsLoader: boolean;
    isLoaderRequired?: boolean;
    setCategoryFiltersLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    categoryFiltersLoading?: boolean;
}

const ReportFiltersLayout: React.FC<IReportFiltersLayout> = ({
    reviewsLoader,
    children,
    isLoaderRequired = false,
    setCategoryFiltersLoading,
    categoryFiltersLoading = false,
}) => {
    const {
        dates,
        DateRange,
        fixedOptions,
        setFixedOptions,
        selectedCategory,
        setSelectedCategory,
        selectedSubCategory,
        setSelectedSubCategory,
        skusData,
        setSkusData,
        selectedSKU,
        setSelectedSKU,
        setReviewsLoader,
        setReviewsSkuData,
        setReviewsBrandData,
        selectedBrand,
        setSelectedBrand,
        selectedCatAdditionalFilters,
        setSelectedCatAdditionalFilters,
        directCompetitorFilter,
        setDirectCompetitorFilter,
        powerSkusFilters,
        setPowerSkusFilter

    } = useContext(mainContext);
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const previousActiveClientId = usePreviousValue(activeClientId);
    const channel = useSelector((state: IChannel) => state.channel.value);
    const [options, setOptions] = useState<IFixedReviewOptions>(
        structuredClone(fixedOptions)
    );
    const [commonSelectAttr] = useState<SelectProps>({
        showSearch: true,
        size: "small",
        optionFilterProp: "label",
        maxTagCount: 1,
        style: { fontSize: 12, width: 200 },
    });

    const [filterConfig] = useState({
        [ReportFilters.CATEGORY]: {
            allValues: fixedOptions.categories,
            setSelected: setSelectedCategory,
        },
        [ReportFilters.SUB_CATEGORY]: {
            allValues: fixedOptions.subCategories,
            setSelected: setSelectedSubCategory,
        },
        [ReportFilters.SKU]: {
            allValues: fixedOptions.skus,
            setSelected: setSelectedSKU,
        },
        [ReportFilters.BRAND]: {
            allValues: fixedOptions.brands,
            setSelected: setSelectedBrand
        }
    });

    const [datePickerAttributes, setDatePickerAttributes] = useState(DATE_COMPARISON_OPTIONS);

    const { selectedDataFrequency, setSelectedDataFrequency } =
        useContext<IMainContext>(mainContext);

    const { activeModule } = getActiveModule()

    const [categoryFiltersData, setCategoryFiltersData] = useState<ICategoryInsightFilters[]>([])

    const [openCategoryAdvFilters, setOpenCategoryAdvFilters] = useState<boolean>(false);

    const [additionalFiltersData, setAdditionalFiltersData] = useState([]);

    const [selectedAdditionalFilters, setSelectedAdditionalFilters] = useState([]);

    const handleReviewsFiltersChange = (
        type: ReportFilters,
        value: string[]
    ) => {
        const getMaxLength = (type: string) => {
            return type === ReportFilters.CATEGORY ||
                type === ReportFilters.SUB_CATEGORY
                ? REVIEW_FILTER_MAX_LIMIT
                : null;
        };

        const handleSelection = (
            key: ReportFilters,
            allValues: IValueString[],
            setSelected: React.Dispatch<React.SetStateAction<any>>
        ) => {
            const maxLength = getMaxLength(type);

            if (maxLength === null || value.length <= maxLength) {
                const { activeModule } = getActiveModule();

                setSelected(value);

                if (
                    activeModule === SubModule.REVIEW_BRAND &&
                    (key === ReportFilters.CATEGORY ||
                        key === ReportFilters.SUB_CATEGORY)
                ) {
                    fetchReviewsBrandData(
                        setReviewsBrandData,
                        setReviewsLoader,
                        fixedOptions
                    );
                }
            }
        };

        const handleCategoryInsightsSelection = (
            type: ReportFilters,
            allValues: IValueString[],
            setSelected: React.Dispatch<React.SetStateAction<any>>
        ) => {

            if (type === ReportFilters.CATEGORY) {
                setSelected(value)
                if (value.length === 0) {
                    filterConfig[ReportFilters.SUB_CATEGORY].setSelected([])
                    filterConfig[ReportFilters.BRAND].setSelected([])
                    // set options
                    setOptions((prev) => {
                        return {
                            ...prev,
                            subCategories: [],
                            brands: []
                        };
                    })
                } else {
                    const correspondingSubCategories = getCategoryMatchingSubCategories(value, categoryFiltersData);
                    // set options
                    setOptions((prev) => {
                        return {
                            ...prev,
                            subCategories: correspondingSubCategories.map(value => ({ value, label: value }))
                        };
                    })
                }
            }

            if (type === ReportFilters.SUB_CATEGORY) {
                setSelected(value)
                if (value.length === 0) {
                    filterConfig[ReportFilters.BRAND].setSelected([])
                    // set options
                    setOptions((prev) => {
                        return {
                            ...prev,
                            brands: []
                        };
                    })
                } else {
                    const correspondingBrands = getCategoryMatchingBrands(value, categoryFiltersData);
                    // set options
                    setOptions((prev) => {
                        return {
                            ...prev,
                            brands: correspondingBrands.map(value => ({ value, label: value }))
                        };
                    })
                }
            }

            if (type === ReportFilters.BRAND) {
                setSelected(value);
            }

        }

        const config = filterConfig[type];
        if (config) {
            if (activeModule === SubModule.CATEGORY_INSIGHTS) {
                handleCategoryInsightsSelection(type, config.allValues, config.setSelected)
            } else {
                handleSelection(type, config.allValues, config.setSelected);
            }

        }
    };

    const setSkusDependentOptions = () => {
        const dependentOptionsExists = !!(
            selectedCategory.length || selectedSubCategory.length
        );
        let skus: Record<string, DefaultOptionType[]> = {};
        if (dependentOptionsExists) {
            const skuOptions: Record<string, string>[] = [];
            const selectedCat = includes(selectedCategory, OptionValues.ALL)
                ? map(fixedOptions.categories, (item) => item.value)
                : selectedCategory;
            const selectedSubcat = includes(
                selectedSubCategory,
                OptionValues.ALL
            )
                ? map(fixedOptions.subCategories, (item) => item.value)
                : selectedSubCategory;

            each(skusData, (item: ISkusData) => {
                if (selectedCategory.includes(OptionValues.ALL) && selectedSubCategory.includes(OptionValues.ALL)) {
                    skuOptions.push({
                        value: item.sku,
                        label: item.sku,
                    });
                } else if (item.sku_tags) {
                    each(selectedCat, (category) => {
                        if (includes(item.sku_tags, category)) {
                            skuOptions.push({
                                value: item.sku,
                                label: item.sku,
                            });
                        }
                    });

                    each(selectedSubcat, (subCategory) => {
                        if (includes(item.sku_tags, subCategory)) {
                            skuOptions.push({
                                value: item.sku,
                                label: item.sku,
                            });
                        }
                    });
                }
            });

            const uniqueArray = Array.from(
                new Set(map(skuOptions, (a) => JSON.stringify(a)))
            ).map((str) => JSON.parse(str));
            skus = {
                skus: uniqueArray,
            };
        } else {
            skus = formatSkusFilters(skusData);
        }

        setFixedOptions((prev: IFixedReviewOptions) => ({ ...prev, ...skus }));
        setOptions((prev: IFixedReviewOptions) => ({ ...prev, ...skus }));
        const avlSkus = map(skus.skus, (item) => item.value);
        setSelectedSKU((prev) =>
            avlSkus.length
                ? includes(avlSkus, prev)
                    ? prev
                    : (avlSkus[0] as string)
                : prev
        );
    };

    const fetchReviewFilters = async () => {
        try {
            const params = getQueryParams();
            const doUrlParamsExist = keys(params).length > 0;
            const activeModule = getActiveModule().activeModule;
            const categoriesFilters = await getReviewsFiltersData(
                ReportFiltersType.CATEGORIES
            );
            if (!categoriesFilters || !categoriesFilters.data)
                throw new Error("Failed to fetch category filters");
            const { categories, subCategories } = formatCategoriesFilters(
                categoriesFilters.data
            );

            let filters = { categories, subCategories }
            if (includes(SKU_DEPENDENT_MODULES, activeModule)) {
                const skusFilters = await getReviewsFiltersData(
                    ReportFiltersType.SKUS
                );
                if (!skusFilters || !skusFilters.data)
                    throw new Error("Failed to fetch SKU filters");
                setSkusData(skusFilters.data);
                const formattedSkus = formatSkusFilters(skusFilters.data);
                filters = { ...filters, ...formattedSkus };
                setSelectedSKU(
                    doUrlParamsExist ? params.sku : formattedSkus.skus[0].value
                );
            }
            setSelectedCategory(
                doUrlParamsExist
                    ? params.category === OptionValues.ALL
                        ? map(categories, (option) => option.value) : params.category?.length > 0 ? params.category : []
                    : []
            );
            setSelectedSubCategory(
                doUrlParamsExist
                    ? params.subcategory === OptionValues.ALL
                        ? map(subCategories, (option) => option.value) : params.subcategory?.length > 0 ? params.subcategory : []
                    : []
            );
            setFixedOptions(filters);
            setOptions(filters);
        } catch (err) {
            console.error(err);
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
        }
    };

    const fetchCategoryInsightsFilters = async () => {
        try {
            setCategoryFiltersLoading(true);
            const module = ProductDeepdiveModules.OVERVIEW;
            const params = getQueryParams();
            const doUrlParamsExist = Object.keys(params).length > 0;
            const categoryInsightsFilters = await fetchCategoryAnalysisFiltersData(module);

            const additionalFiltersData = groupValuesByFeature(categoryInsightsFilters);

            setAdditionalFiltersData(additionalFiltersData)

            setCategoryFiltersData(categoryInsightsFilters)

            if (categoryInsightsFilters.length) {

                const { categories, subCategories, brands, features } =
                    getCategoryInsightsFilters(categoryInsightsFilters);

                const filters = formatCategoryInsightsFilters(
                    categories,
                    subCategories,
                    brands,
                    features
                );

                setFixedOptions(filters);
                setOptions(filters);
                const firstCategory = [categories[0]];

                const correspondingSubCategories = getCategoryMatchingSubCategories(firstCategory, categoryInsightsFilters);

                const firstSubCategory = [correspondingSubCategories[0]]

                const correspondingBrands = getCategoryMatchingBrands(firstSubCategory, categoryInsightsFilters);

                const firstBrand = [correspondingBrands[0]]


                if (previousActiveClientId !== undefined && previousActiveClientId !== activeClientId) {

                    setSelectedCategory(
                        firstCategory
                    );

                    setSelectedSubCategory(
                        firstSubCategory
                    );

                    setSelectedBrand(
                        firstBrand
                    );
                } else {

                    setSelectedCategory(
                        doUrlParamsExist ? typeof params.category === "string" ? [params.category] : params.category?.length
                            ? params.category : []
                            : firstCategory
                    );

                    setSelectedSubCategory(
                        doUrlParamsExist
                            ? typeof params.subcategory === "string" ? [params.subcategory] : params.subcategory?.length ?
                                params.subcategory : []
                            : firstSubCategory
                    );

                    setSelectedBrand(
                        doUrlParamsExist
                            ? typeof params.brands === "string" ? [params.brands] :
                                params.brands?.length ? params.brands : []
                            : firstBrand
                    );
                }
            }

            setCategoryFiltersLoading(false);
        } catch (err) {
            setCategoryFiltersLoading(false);
            console.error(err);
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
        }
    };



    useEffect(() => {

        if (activeModule === SubModule.CATEGORY_INSIGHTS) {
            fetchCategoryInsightsFilters()
        } else {
            fetchReviewFilters();
        }
    }, [activeClientId, channel]);

    useEffect(() => {
        includes(SKU_DEPENDENT_MODULES, getActiveModule().activeModule) &&
            setSkusDependentOptions();
    }, [selectedCategory, selectedSubCategory]);

    useEffect(() => {
        const params: Record<string, string[]> = {};
        const { sku } = getQueryParams();

        if (selectedCategory.length) {
            params.category = selectedCategory;
        }

        if (selectedSubCategory.length) {
            params.subcategory = selectedSubCategory;
        }

        if (selectedBrand.length) {
            params.brands = selectedBrand
        }

        if (
            includes(SKU_DEPENDENT_MODULES, getActiveModule().activeModule) &&
            selectedSKU.length &&
            sku !== selectedSKU
        ) {
            params.sku = [selectedSKU];
            const paramsCondition =
                sku &&
                sku.length &&
                selectedSKU &&
                selectedSKU.length &&
                sku !== selectedSKU;
            const customDateCondition = dates !== 1;
            const dateRangeCondition = DateRange !== undefined;
            const apiCallCond =
                paramsCondition || customDateCondition || dateRangeCondition;
            setReviewsLoader(true);
            apiCallCond &&
                fetchReviewsSkuData(
                    setReviewsSkuData,
                    setReviewsLoader,
                    selectedSKU
                );
        }

        keys(params).length > 0 && setQueryParams(params);
    }, [
        selectedSKU,
        selectedCategory,
        selectedSubCategory,
        selectedBrand,
        channel,
        activeClientId,
        dates,
        DateRange,
    ]);

    function tagRender(props: Record<string, any>, selectedArrLength: number) {
        const { label } = props;

        if (label) {
                return (
                    <span className="mx-1">
                        {selectedArrLength > 1
                            ? `${label.substring(0, 5)},`
                            : `${label.substring(0, 10)}`}
                    </span>
                );
        }
    }

    const handleCompareWith = () => {
        setDatePickerAttributes((prev) => ({
            ...prev,
            isCompareWithChecked: !prev.isCompareWithChecked,
        }));
    };

    const handleAdvFilterCheckboxChange = (selectedValues: string[], featureName: string) => {
        setSelectedAdditionalFilters((prev) => {

            const updated = prev.filter((item) => item.feature !== featureName);

            if (selectedValues.length > 0) {
                return [...updated, { feature: featureName, values: selectedValues }];
            }
            return updated;
        });
    };

    const handleApplyAdditionalFilter = () => {
        const formatted = selectedAdditionalFilters.reduce((acc, curr) => {
            acc[curr.feature] = curr.values;
            return acc;
        }, {} as Record<string, string[]>);
        setSelectedCatAdditionalFilters(formatted);
        setOpenCategoryAdvFilters(false);
    };

    const handleClearAdditionalFilters = () => {
        setSelectedAdditionalFilters([])
        setSelectedCatAdditionalFilters({});
    }


    return (
        <Layout>
            <div className="d-flex justify-content-between bg-white w-100 h-45 px-3 py-1 shadow-sm rounded">
                <div className="d-flex justify-content-start align-items-center w-100">
                    <div>
                        <Select
                            className="report-filters-select"
                            id="category-select"
                            {...commonSelectAttr}
                            mode="multiple"
                            value={selectedCategory}
                            options={options.categories as DefaultOptionType[]}
                            tagRender={(props) =>
                                tagRender(props, selectedCategory.length)
                            }
                            onChange={(value) =>
                                handleReviewsFiltersChange(
                                    ReportFilters.CATEGORY,
                                    value
                                )
                            }
                            disabled={categoryFiltersLoading}
                            prefix="Category:"
                            allowClear
                            loading={categoryFiltersLoading}
                        />
                    </div>
                    <div className="mx-1 d-flex flex-wrap align-items-center">
                        <Select
                            className="report-filters-select"
                            id="sub-category-select"
                            {...commonSelectAttr}
                            mode="multiple"
                            value={selectedSubCategory}
                            tagRender={(props) =>
                                tagRender(props, selectedSubCategory.length)
                            }
                            options={
                                options.subCategories as DefaultOptionType[]
                            }
                            onChange={(value) =>
                                handleReviewsFiltersChange(
                                    ReportFilters.SUB_CATEGORY,
                                    value
                                )
                            }
                            disabled={categoryFiltersLoading}
                            prefix="Sub-Category:"
                            allowClear
                            loading={categoryFiltersLoading}
                        />
                    </div>
                    {!includes(
                        SKU_DEPENDENT_MODULES,
                        getActiveModule().activeModule
                    ) && (
                        <div>
                            <Select
                                className="report-filters-select"
                                id="brands-select"
                                {...commonSelectAttr}
                                value={selectedBrand}
                                tagRender={(props) =>
                                    tagRender(props, selectedBrand.length)
                                }
                                options={options.brands as DefaultOptionType[]}
                                mode="multiple"
                                onChange={(value) =>
                                    handleReviewsFiltersChange(
                                        ReportFilters.BRAND,
                                        value
                                    )
                                }
                                disabled={categoryFiltersLoading}
                                prefix="Brand:"
                                allowClear
                                loading={categoryFiltersLoading}
                            />
                        </div>
                    )}
                    {includes(
                        SKU_DEPENDENT_MODULES,
                        getActiveModule().activeModule
                    ) && (
                        <div>
                            <Select
                                className="report-filters-sku-select"
                                id="skus-select"
                                {...commonSelectAttr}
                                value={selectedSKU}
                                options={options.skus as DefaultOptionType[]}
                                onChange={(value) =>
                                    handleReviewsFiltersChange(
                                        ReportFilters.SKU,
                                        value
                                    )
                                }
                                disabled={reviewsLoader}
                                prefix="SKUs:"
                            />
                        </div>
                    )}
                    <div className="mx-2">
                        <Radio.Group
                            size="small"
                            value={selectedDataFrequency}
                            onChange={(e) =>
                                setSelectedDataFrequency(e.target.value)
                            }
                            optionType="button"
                            buttonStyle="solid"
                        >
                            {Object.values(FrequencySlicerOptions).map(
                                (option) => (
                                    <Radio.Button
                                        value={option}
                                        style={{ fontSize: "0.7rem" }}
                                        key={option}
                                    >
                                        {option}
                                    </Radio.Button>
                                )
                            )}
                        </Radio.Group>

                        {/* <div>
    <label
        className="form-label me-2 text-secondary"
        htmlFor="current-date-picker"
    >
        Current Period
    </label>
    <RangePicker
        id="current-date-picker"
        style={commonSelectAttr.style}
        size={commonSelectAttr.size}
    />
</div>
<Checkbox
    className="mx-2 mb-1"
    checked={datePickerAttributes.isCompareWithChecked}
    onChange={handleCompareWith}
>
    Compare with
</Checkbox>
{datePickerAttributes.isCompareWithChecked && (
    <div>
        <label
            className="form-label me-2 text-secondary"
            htmlFor="previous-date-picker"
        >
            Previous Period
        </label>
        <RangePicker
            id="previous-date-picker"
            style={commonSelectAttr.style}
            size={commonSelectAttr.size}
        />
    </div>
)} */}
                    </div>
                    {!includes(
                        SKU_DEPENDENT_MODULES,
                        getActiveModule().activeModule
                    ) && (
                        <div>
                            <Checkbox
                                className="category-overview-checkbox-filters"
                                checked={directCompetitorFilter}
                                onChange={(e) =>
                                    setDirectCompetitorFilter(e.target.checked)
                                }
                                disabled={!categoryFiltersData?.length}
                            >
                                <span>Direct Competitors</span>
                            </Checkbox>
                            <Checkbox
                                className="category-overview-checkbox-filters"
                                checked={powerSkusFilters}
                                onChange={(e) =>
                                    setPowerSkusFilter(e.target.checked)
                                }
                                disabled={!categoryFiltersData?.length}
                            >
                                <span>Power SKUs</span>
                            </Checkbox>
                        </div>
                    )}
                    {!includes(
                        SKU_DEPENDENT_MODULES,
                        getActiveModule().activeModule
                    ) && (
                        <div
                            className="category-overview-additional-filters-wrapper "
                            style={{ marginLeft: "auto" }}
                        >
                            <Tooltip
                                title={
                                    Object.keys(selectedCatAdditionalFilters)
                                        .length > 0
                                        ? (
                                              Object.entries(
                                                  selectedCatAdditionalFilters
                                              ) as [string, string[]][]
                                          )
                                              .map(
                                                  ([key, values]) =>
                                                      `${key}: ${values.join(
                                                          ", "
                                                      )}`
                                              )
                                              .join(" | ")
                                        : "No filters selected"
                                }
                                mouseEnterDelay={0.2}
                                placement="bottom"
                                className="category-advance-filter-tooltip"
                            >
                                <Badge
                                    count={Object.values(
                                        selectedCatAdditionalFilters
                                    ).reduce(
                                        (total, arr) => total + arr.length,
                                        0
                                    )}
                                    color="blue"
                                    overflowCount={99}
                                    size="small"
                                >
                                    <Button
                                        className="category-overview-additional-filters-btn"
                                        icon={
                                            <GiSettingsKnobs
                                                style={{
                                                    transform: "rotate(90deg)",
                                                }}
                                            />
                                        }
                                        onClick={() =>
                                            setOpenCategoryAdvFilters(true)
                                        }
                                        disabled={!categoryFiltersData?.length}
                                    >
                                        Filters
                                    </Button>
                                </Badge>
                            </Tooltip>
                            <Drawer
                                title="Additional Filters"
                                placement="right"
                                closable
                                onClose={() => setOpenCategoryAdvFilters(false)}
                                open={openCategoryAdvFilters}
                                styles={{
                                    body: {
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "100%",
                                    },
                                }}
                            >
                                <div style={{ flex: 1, overflowY: "auto" }}>
                                    {additionalFiltersData.map((group) => (
                                        <div
                                            key={group.feature}
                                            style={{ marginBottom: 16 }}
                                        >
                                            <Title
                                                level={5}
                                                style={{ marginBottom: 4 }}
                                            >
                                                {group?.feature.charAt(0).toUpperCase() + group?.feature.slice(1)}
                                            </Title>
                                            <Select
                                                mode="multiple"
                                                style={{ width: "100%" }}
                                                placeholder={`Select ${group.feature}`}
                                                value={
                                                    selectedAdditionalFilters.find(
                                                        (item) =>
                                                            item.feature ===
                                                            group.feature
                                                    )?.values || []
                                                }
                                                onChange={(selectedValues) =>
                                                    handleAdvFilterCheckboxChange(
                                                        selectedValues,
                                                        group.feature
                                                    )
                                                }
                                                options={group.values.map(
                                                    (val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    })
                                                )}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div
                                    style={{
                                        paddingTop: 16,
                                        borderTop: "1px solid #f0f0f0",
                                    }}
                                    className="d-flex justify-content-between"
                                >
                                    <Button
                                        className="category-overview-additional-filters-apply-btn"
                                        type="primary"
                                        onClick={handleApplyAdditionalFilter}
                                    >
                                        Apply
                                    </Button>
                                    <Button
                                        className="category-overview-additional-filters-apply-btn"
                                        type="primary"
                                        onClick={handleClearAdditionalFilters}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </Drawer>
                        </div>
                    )}
                </div>
            </div>
            <div
                className="main-content-body subheader-fixed py-3 px-4"
                style={{
                    height: "85vh",
                    ...(!reviewsLoader ? { backgroundColor: "#F8F9FA" } : {}),
                }}
            >
                {isLoaderRequired && reviewsLoader ? (
                    <div className="d-flex align-items-center justify-content-center h-100">
                        <span
                            style={{
                                transform: "translate(-50%)",
                                marginBottom: "15%",
                            }}
                        >
                            <AgGridLoaderComponent />
                        </span>
                    </div>
                ) : (
                    children
                )}
            </div>
        </Layout>
    );
};

export default ReportFiltersLayout;
